import React, { FC, useEffect, useState } from 'react';
import type {
  OrganisationCreditPolicy,
  OrganisationCreditPolicyMode,
  OrganisationSeatPlan,
  UpdateOrganisationCreditPolicyRequest,
} from '@dreamai/types';
import {
  MODE_DESCRIPTIONS,
  MODE_LABELS,
  formatCredits,
} from './organisationCreditLabels';
import { planCredits } from './organisationPlans';

interface OrganisationCreditPolicyFormProps {
  /** Groups are switched on for this organisation (the backend lists them) */
  groupsAvailable: boolean;
  policy: OrganisationCreditPolicy;
  seatPlan: OrganisationSeatPlan | null;
  busy: boolean;
  onSave: (patch: UpdateOrganisationCreditPolicyRequest) => Promise<boolean>;
}

/** Empty string ↔ null ("automatic" / "no cap"); undefined when invalid. */
export function parseOptionalNumber(value: string): number | null | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isInteger(n) && n >= 0 ? n : undefined;
}

/**
 * How organisation credits are shared between members. The default (one
 * shared balance, no caps) needs no configuration; this form sits in the
 * collapsed "Advanced" section of Plan & billing for organisations that want
 * per-member limits.
 */
const OrganisationCreditPolicyForm: FC<OrganisationCreditPolicyFormProps> = ({
  groupsAvailable,
  policy,
  seatPlan,
  busy,
  onSave,
}) => {
  const [mode, setMode] = useState<OrganisationCreditPolicyMode>(policy.mode);
  const [allocation, setAllocation] = useState(
    policy.defaultAllocation === null ? '' : String(policy.defaultAllocation),
  );
  const [overflow, setOverflow] = useState(
    policy.overflowPerMemberCap === null
      ? ''
      : String(policy.overflowPerMemberCap),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMode(policy.mode);
    setAllocation(
      policy.defaultAllocation === null ? '' : String(policy.defaultAllocation),
    );
    setOverflow(
      policy.overflowPerMemberCap === null
        ? ''
        : String(policy.overflowPerMemberCap),
    );
  }, [policy]);

  const allocates = mode !== 'pooled';
  const poolAccess = mode !== 'allocated';
  const allocationValue = parseOptionalNumber(allocation);
  const overflowValue = parseOptionalNumber(overflow);

  const dirty =
    mode !== policy.mode ||
    (allocates && allocationValue !== policy.defaultAllocation) ||
    (poolAccess && overflowValue !== policy.overflowPerMemberCap);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (allocationValue === undefined || overflowValue === undefined) {
      setError('Credit amounts must be whole numbers, or left blank.');
      return;
    }
    const patch: UpdateOrganisationCreditPolicyRequest = { mode };
    if (allocates) patch.defaultAllocation = allocationValue;
    if (poolAccess) patch.overflowPerMemberCap = overflowValue;
    await onSave(patch);
  };

  const autoAllocation = seatPlan
    ? `${formatCredits(planCredits(policy.defaultPlanMultiplier, seatPlan.creditsPerUnit))} credits for a member on the organisation plan`
    : 'the credits their plan buys';

  return (
    <form
      className='organisation-subcard'
      data-testid='organisation-credit-policy'
      onSubmit={handleSubmit}
    >
      <p className='organisation-card-text'>
        By default all the credits your plans buy go into one shared balance
        that every member can use. Change this if you want each member limited
        to their own share.
        {groupsAvailable &&
          ' Groups can set their own limits for their members.'}
      </p>

      <label className='organisation-field'>
        <span className='field-label'>Sharing</span>
        <select
          className='field-select'
          value={mode}
          disabled={busy}
          onChange={(e) =>
            setMode(e.target.value as OrganisationCreditPolicyMode)
          }
        >
          {(Object.keys(MODE_LABELS) as OrganisationCreditPolicyMode[]).map(
            (m) => (
              <option key={m} value={m}>
                {MODE_LABELS[m]}
              </option>
            ),
          )}
        </select>
        <span className='organisation-field-hint'>
          {MODE_DESCRIPTIONS[mode]}
        </span>
      </label>

      {(allocates || poolAccess) && (
        <div className='organisation-field-grid'>
          {allocates && (
            <label className='organisation-field'>
              <span className='field-label'>Credits per member each month</span>
              <input
                type='number'
                className='field-input'
                min={0}
                placeholder='Automatic'
                value={allocation}
                disabled={busy}
                onChange={(e) => setAllocation(e.target.value)}
              />
              <span className='organisation-field-hint'>
                Leave blank for automatic: {autoAllocation}.
              </span>
            </label>
          )}

          {poolAccess && (
            <label className='organisation-field'>
              <span className='field-label'>
                Shared-balance limit per member each month
              </span>
              <input
                type='number'
                className='field-input'
                min={0}
                placeholder='No limit'
                value={overflow}
                disabled={busy}
                onChange={(e) => setOverflow(e.target.value)}
              />
              <span className='organisation-field-hint'>
                {allocates
                  ? 'How much of the shared balance a member may use once their own credits are spent. Blank means no limit.'
                  : 'How much of the shared balance each member may use in a month. Blank means no limit.'}
              </span>
            </label>
          )}
        </div>
      )}

      {error && (
        <p className='organisation-feedback error' role='alert'>
          {error}
        </p>
      )}

      <div className='organisation-actions'>
        <button type='submit' className='btn-primary' disabled={busy || !dirty}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
};

export default OrganisationCreditPolicyForm;
